import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors } from "@angular/forms";
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { AccountDetailsService, SubTierService } from '../../../service/account-details/account.details.service';
import { SDSSelectedItemModel, SdsDialogService } from '@gsa-sam/components';
import { Router } from '@angular/router';
import { GlobalConfig, ToastrService } from 'ngx-toastr';

@Component({
  selector: 'sam-account-details-update',
  templateUrl: './account-details-update.component.html',
  styleUrls: ['./account-details-update.component.scss'],
})
export class AccountDetailsUpdateComponent
  implements OnInit, OnChanges, AfterViewInit
{
  @Input() user: any;
  @Input() orgData: any;
  @Output() onCancel: EventEmitter<any> = new EventEmitter<any>();
  @Output() onUpdate: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('orgPicker') orgPicker;

  mainForm = new FormGroup({});
  model: any = {};
  options: FormlyFormOptions = {};
  fields: FormlyFieldConfig[];
  department: string = '';
  subTier: string = '';
  agencyID = [];
  toastoptions: GlobalConfig;
  subTierModel: any;
  nameRegEx = new RegExp("^[a-zA-Z_'-][a-zA-Z_ '-]*[a-zA-Z_'-]$");
  subTierSelection = false;

  constructor(
    private accountDetailsService: AccountDetailsService,
    public dialog: SdsDialogService,
    public router: Router,
    public subTierService: SubTierService,
    private toastr: ToastrService
  ) {
    // Intentionally blank. Comment is here to satisfy SonarQube
  }

  ngOnInit(): void {
    this.userInfo();
    this.fields = [];
    if (this.user?.isIdentityVerified) {
      this.fields.push(this.getFirstNameConfig(this.user?.firstName, true));
      this.fields.push(this.getLastNameConfig(this.user?.lastName, true));
      this.fields.push(this.getEmailText(this.user?.email));
      this.fields.push(
        this.getBusinessPhoneNumberConfig(
          this.user?.workPhone,
          this.user?.workPhoneExt,
          true
        )
      );
    } else {
      this.fields.push(this.getFirstNameConfig(this.user?.firstName, false));
      this.fields.push(this.getLastNameConfig(this.user?.lastName, false));
      this.fields.push(this.getEmailText(this.user?.email));
      this.fields.push(
        this.getBusinessPhoneNumberConfig(
          this.user?.workPhone,
          this.user?.workPhoneExt,
          false
        )
      );
    }

    this.getDetails(this.orgData);
    if (this.user?.isGov) {
      this.fields.push(this.getGovConfig(this.department));
      this.fields.push(this.getGovConfigMobile(this.department));

      if (this.user?.org && this.user?._link) {
        this.subTierModel = new SDSSelectedItemModel([
          {
            key: this.user?.org?.orgKey,
            value: this.user?.org?.fpdsCode + ' - ' + this.user?.org?.name,
            subtext: '',
            highlighted: true,
          },
        ]);
      }
    }
    this.toastoptions = this.toastr.toastrConfig;
    this.toastoptions.iconClasses = {
      error: 'sds-toast--error',
      info: 'sds-toast--info',
      success: 'sds-toast--success',
      warning: 'sds-toast--warning',
    };
    this.toastoptions.closeButton = true;
    this.subToMainFormChanges();
  }

  ngAfterViewInit(): void {
    // Intentionally blank. Comment is here to satisfy SonarQube
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.orgData) {
      this.updateDetails();
    }
  }

  getDetails(orgData: any) {
    this.department = orgData?.length ? orgData[0]?.org?.name || '' : null;
  }

  updateDetails() {
    if (this.mainForm?.value && Object.keys(this.mainForm.value)) {
      this.getDetails(this.orgData);

      if (this.mainForm.get('department')) {
        (<any>this.mainForm.get('department'))?.setValue(this.department);
      }
      if (this.mainForm.get('subTier')) {
        (<any>this.mainForm.get('subTier'))?.setValue(this.subTier);
      }
    }
  }

  getFirstNameConfig(value: string, disabled: boolean) {
    return {
      fieldGroupClassName: 'grid-row',
      fieldGroup: [
        {
          className: 'desktop:grid-col-7 tablet:grid-col-12 grid-col-12',
          key: 'firstName',
          type: 'input',
          defaultValue: value || '',
          props: {
            label: 'First Name',
            required: true,
            announceLabel: true,
            disabled: disabled,
          },
          validators: {
            firstName: {
              expression: this.patternMatch,
              message: this.errorMessage,
            },
          },
        },
      ],
    };
  }

  getLastNameConfig(value: string, disabled: boolean) {
    return {
      fieldGroupClassName: 'grid-row',
      fieldGroup: [
        {
          className: 'desktop:grid-col-7 tablet:grid-col-12 grid-col-12',
          key: 'lastName',
          type: 'input',
          defaultValue: value || '',
          props: {
            label: 'Last Name',
            required: true,
            announceLabel: true,
            disabled: disabled,
          },
          validators: {
            lastName: {
              expression: this.patternMatch,
              message: this.errorMessage,
            },
          },
        },
      ],
    };
  }

  patternMatch = (c: AbstractControl) =>
    !!c.value ? this.nameRegEx.test(c.value) : false;
  errorMessage = (error: any, field: FormlyFieldConfig) =>
    field.formControl.value
      ? 'This field allows letters, apostrophes(`) and hyphens(-).'
      : 'This field is required.';

  getEmailText(value: string) {
    return {
      fieldGroupClassName: 'grid-row',
      fieldGroup: [
        {
          className: 'email-text',
          template: `<h5>Email</h5><span>You can't change the email address on your account.
                    <a class="usa-link" target="_blank" href="https://www.fsd.gov/gsafsd_sp?id=gsafsd_kb_articles&sys_id=840be1bf1b0b2954cc45ea04bc4bcbaa" [hideIcon]="true">Why?</a>
                    </span>
                    <h5>${value || ''}</h5>`,
        },
      ],
    };
  }

  phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
    const phoneNumber = control.value;
    const countryCode = control.parent?.get('countryCode')?.value;

    if (!countryCode && !phoneNumber) {
      return null;
    }

    if (!countryCode && phoneNumber) {
      return { countryCodeRequired: 'Country code is required if phone number is entered' };
    }

    if (countryCode && !phoneNumber) {
      return { phoneNumberRequired: 'Phone number is required if country code is entered' };
    }

    if (countryCode === '1' || countryCode === '001') {
      if (!/^\d{10}$/.test(phoneNumber)) {
        return { invalidPhoneNumber: 'North American phone numbers must be 10 digits' }
      }
    } else {
      if (!/^\d{4,}$/.test(phoneNumber)) {
        return { invalidPhoneNumber: 'Phone number must be at least 4 digits for country codes other than 1' };
      }
    }

    return null;
  }

  getBusinessPhoneNumberConfig(
    workPhone: string,
    workPhoneExt: string,
    disabled: boolean
  ) {
    const workNum =
      workPhone?.substring(workPhone.length - 10, workPhone.length) || '';

    let countryCode = workPhone?.substring(0, workPhone.length - 10) || '';
    countryCode =
      countryCode !== ''
        ? ['000', '00' + countryCode, '0' + countryCode, countryCode][
            countryCode.length
          ]
        : '';

    return {
      fieldGroupClassName: 'grid-row grid-gap-2',
      fieldGroup: [
        {
          className: 'grid-col-12',
          template: `<h5>Business Phone</h5><span>Country Code is 1 for USA and North America</span>`,
        },
        {
          className: 'grid-col-12 tablet:grid-col-3 desktop:grid-col-2',
          key: 'countryCode',
          type: 'input',
          defaultValue: countryCode || '',
          props: {
            label: 'Country Code',
            required: false,
            type: 'number',
            placeholder: 'ex-123',
            pattern: '^\\d{1,3}$',
            announceLabel: true,
            disabled: disabled,
            hideOptional: true,
          },
          validation: {
            messages: {
              pattern: 'Country codes must be 3 digits or fewer'
            }
          }
        },
        {
          className:
            'grid-col-12 tablet:grid-col-9 desktop:grid-col-4 custom-error-margin',
          key: 'workNum',
          type: 'input',
          defaultValue: workNum || '',
          props: {
            label: 'Phone',
            required: false,
            type: 'number',
            placeholder: 'ex-1234567890',
            announceLabel: true,
            disabled: disabled,
            hideOptional: true,
          },
          validators: {
            validation: [this.phoneNumberValidator]
          },
          validation: {
            messages: {
              invalidPhoneNumber: error => error,
              countryCodeRequired: error => error,
              phoneNumberRequired: error => error
            },
          },
          hooks: {
            onInit: field => {
              const phoneControl = field.formControl;
              const countryCodeControl = field.form.get('countryCode');

              // Re-evaluate the phone number's validity when the country code changes
              countryCodeControl.valueChanges.subscribe(() => {
                phoneControl.updateValueAndValidity();
              });

              // Mark phoneControl as touched to show error messages on form load
              setTimeout(() => {
                phoneControl.markAsTouched();
              });
            }
          }
        },
        {
          className:
            'margin-bottom-0 grid-col-3 desktop:grid-col-2 display-none desktop:display-inline-block',
          key: 'workPhoneExt',
          type: 'input',
          defaultValue: workPhoneExt || '',
          props: {
            label: 'Extension',
            type: 'number',
            pattern: '\\d+$',
            placeholder: 'ex-1234',
            announceLabel: true,
            disabled: disabled,
            hideOptional: true,
          },
        },
        {
          className:
            ' margin-top-(-1) grid-col-8 display-block desktop:display-none',
          type: 'button',
          hideExpression: (model) => this.model.showExtension,
          props: {
            type: 'button',
            text: 'Show Extension',
            btnType: 'info',
            onClick: ($event) => {
              $event.preventDefault();
              this.model.showExtension = 'show';
              return false;
            },
          },
        },
        {
          className:
            'margin-top-(-1) grid-col-8 display-block desktop:display-none',
          type: 'input',
          key: 'workPhoneExt',
          defaultValue: workPhoneExt || '',
          hideExpression: (model) => !this.model.showExtension,
          props: {
            label: 'Extension',
            type: 'number',
            //pattern: '\\d{10}',
            placeholder: 'ex-1234',
            announceLabel: true,
            disabled: disabled,
            hideOptional: true,
          },
        },
      ],
    };
  }

  getGovConfig(departmentName = '') {
    return {
      fieldGroupClassName: 'grid-row organization-information',
      fieldGroup: [
        {
          fieldGroupClassName: 'grid-col-7 mobile-lg:none',
          fieldGroup: [
            {
              template: `<h2>Organization Information</h2><span>Select the office you work for.</span><br>
                            <strong>Note: </strong><span>Few people will need to be associated at the department or sub-tier level.</span>`,
            },
            {
              className: 'grid-col-12 mobile-lg:none',
              key: 'department',
              type: 'input',
              defaultValue: departmentName,
              props: {
                label: 'Department/Ind.Agency',
                disabled: true,
                hideOptional: true,
                announceLabel: true,
              },
            },
          ],
        },
        {
          fieldGroupClassName: 'grid-col-5 mobile-lg:none',
          fieldGroup: [
            {
              template: `<h2>Tips to find your office:</h2>
                            <strong>Enter office codes or keywords in the search field to select the lowest level organization where you work.<br><br>
                             <ul>
                              <li>Office Name</li>
                              <li>CGAC</li>
                              <li>AAC</li>
                              <li>Agency Code</li>
                            </ul></strong>`,
            },
          ],
        },
      ],
    };
  }

  getGovConfigMobile(departmentName = '') {
    return {
      fieldGroupClassName: 'grid-row desktop:display-none tablet:display-none',
      fieldGroup: [
        {
          fieldGroup: [
            {
              className: 'grid-col-12',
              template: `<h2>Organization Information</h2><span>Select the office you work for.</span><br>
                          <strong>Note: </strong><span>Few people will need to be associated at the department or sub-tier level.</span>`,
            },
            {
              className: 'grid-col-12',
              template: `<h2>Tips to find your office:</h2>
                          <strong>Enter office codes or keywords in the search field to select the lowest level organization where you work.<br><br>
                           <ul>
                            <li>Office Name</li>
                            <li>CGAC</li>
                            <li>AAC</li>
                            <li>Agency Code</li>
                          </ul></strong>`,
            },
            {
              className: 'grid-col-12',
              key: 'department',
              type: 'input',
              defaultValue: departmentName,
              props: {
                label: 'Department/Ind.Agency',
                disabled: true,
                hideOptional: true,
                announceLabel: true,
              },
            },
          ],
        },
      ],
    };
  }

  userInfo() {
    this.model.lastName = this.user?.lastName;
    this.model.email = this.user?.email;
    this.model.fullName = this.user?.fullName;
    this.model.firstName = this.user?.firstName;
    this.model.departmentID = this.user?.departmentID;
    this.model.officeID = this.user?.officeID || 0;
    this.model.isGov = this.user?.isGov;
    this.model.isProfileComplete = this.user?.isProfileComplete;
    this.model.loginId = this.user?.loginId;
    this.model.userInfoId = this.user?.userInfoId;
    this.model.isIdentityVerified = this.user?.isIdentityVerified;
    this.model.lastLoginDate = this.user?.lastLoginDate;
    this.model.isIdentityVerifiedException =
      this.user?.isIdentityVerifiedException;
    this.model.isPivCac = this.user?.isPivCac;
    this.model.isTempProfile = this.user?.isTempProfile;
    this.model.isUatSigned = this.user?.isUatSigned;
  }

  submit() {
    this.model.workPhone = `${this.mainForm?.value['countryCode']}${this.mainForm?.value['workNum']}`;
    this.model.workPhoneExt = this.mainForm?.value['workPhoneExt'];
    this.model.agencyID = Array.isArray(this.subTierModel)
      ? this.subTierModel[0]?.key
      : this.subTierModel?.items[0]?.key;

    if (this.subTierModel?.length === 0 && this.user?.isGov) {
      this.model.agencyID = 0;
    }

    const payload = JSON.parse(JSON.stringify(this.model));
    if (payload.countryCode) {
      delete payload.countryCode;
    }

    this.accountDetailsService.updateUserDetails(payload).subscribe({
      next: (resp: any) => {
        this.toastr.success(
          'You have successfully updated your information.',
          ''
        );
        this.onUpdate.emit(payload);
      },
      error: (err: any) => console.log(err),
    });
  }

  get isFormValid() {
    if (this.user?.isIdentityVerified) {
      return this.subTierSelection;
    } else {
      return this.mainForm.valid && this.subTierSelection;
    }
  }

  subToMainFormChanges(){
    this.mainForm.valueChanges.subscribe(() => {
      this.subTierSelection = true;
    });
  }

}
